import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FiArrowRight } from 'react-icons/fi';
import { Container } from '../ui';
import PropertyCard from '../PropertyCard';
import { Property } from '../../types';
import { splitByPurpose } from './locationModel';
import { Band, Eyebrow, SectionHeading, goldText, hairline, ink } from './tokens';

type Purpose = 'sale' | 'rental';

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.5rem;
  margin-bottom: 2.25rem;
`;

const Tabs = styled.div`
  display: inline-flex;
  padding: 5px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => hairline(theme)};
  background: #fff;
`;

const Tab = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  min-height: 44px;
  padding: 0 1.25rem;
  border: none;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.95rem;
  background: ${({ theme, $active }) => ($active ? ink(theme) : 'transparent')};
  color: ${({ theme, $active }) => ($active ? '#fff' : theme.colors.neutral[700])};
  transition: background 0.2s ease, color 0.2s ease;

  span {
    font-variant-numeric: tabular-nums;
    font-size: 0.8rem;
    padding: 0.1rem 0.5rem;
    border-radius: 999px;
    background: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.neutral[100])};
    color: ${({ theme, $active }) => ($active ? ink(theme) : theme.colors.neutral[600])};
  }

  &:hover { color: ${({ theme, $active }) => ($active ? '#fff' : ink(theme))}; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.5rem;

  @media (max-width: 1000px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const Empty = styled.div`
  padding: 2.5rem;
  border: 1px dashed ${({ theme }) => hairline(theme)};
  border-radius: 18px;
  text-align: center;
  color: ${({ theme }) => theme.colors.neutral[600]};

  a {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    margin-top: 0.75rem;
    font-weight: 600;
    color: ${({ theme }) => goldText(theme)};
  }
`;

const Count = styled.p`
  margin-top: 0.6rem;
  color: ${({ theme }) => theme.colors.neutral[600]};
`;

interface Props {
  name: string;
  properties: Property[];
}

const LABELS: Record<Purpose, string> = { sale: 'À venda', rental: 'Para alugar' };

const headingFor = (available: Purpose[]): string => {
  if (available.length > 1) return 'Imóveis à venda e para alugar';
  if (available[0] === 'rental') return 'Imóveis para alugar';
  return 'Imóveis à venda';
};

const LocationListings: React.FC<Props> = ({ name, properties }) => {
  const groups = useMemo(() => splitByPurpose(properties), [properties]);
  const available = (['sale', 'rental'] as Purpose[]).filter((key) => groups[key].length > 0);
  const [chosen, setChosen] = useState<Purpose | null>(null);
  const active = chosen && available.includes(chosen) ? chosen : available[0];
  const shown = active ? groups[active] : [];

  return (
    <Band id="imoveis" aria-labelledby="local-imoveis">
      <Container>
        <Head>
          <div>
            <Eyebrow>{name}</Eyebrow>
            <SectionHeading id="local-imoveis">{headingFor(available)}</SectionHeading>
            {properties.length > 0 && (
              <Count>
                {properties.length === 1 ? '1 imóvel' : `${properties.length} imóveis`} no catálogo agora
              </Count>
            )}
          </div>
          {available.length > 1 && (
            <Tabs role="tablist" aria-label="Finalidade">
              {available.map((key) => (
                <Tab
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={key === active}
                  aria-controls="local-imoveis-lista"
                  $active={key === active}
                  onClick={() => setChosen(key)}
                >
                  {LABELS[key]} <span>{groups[key].length}</span>
                </Tab>
              ))}
            </Tabs>
          )}
        </Head>
        {shown.length > 0 ? (
          <Grid id="local-imoveis-lista" role={available.length > 1 ? 'tabpanel' : undefined}>
            {shown.map((item) => <PropertyCard key={item.id} property={item} />)}
          </Grid>
        ) : (
          <Empty>
            Nenhum imóvel disponível neste momento.
            <div>
              <Link to="/imoveis">Ver todo o catálogo <FiArrowRight size={15} /></Link>
            </div>
          </Empty>
        )}
      </Container>
    </Band>
  );
};

export default LocationListings;
